<script context="module">
    /**
     * Utility to apply function to all of tree nodes
     * @param {TreeNode[] | TreeNode} tree root node or array of root nodes of traversed tree
     * @param {function(TreeNode, any)} fn callback to be called for each tree node
     * @param {any} previousValue result of parent
     */
    export function traverseTree(tree, fn, previousValue) {
        if (Array.isArray(tree)) {
            tree.forEach(node => traverseTree(node, fn, previousValue));
        } else {
            previousValue = fn(tree, previousValue);
            traverseTree(tree.children, fn, previousValue);
        }
    }
</script>

<script>
    // Licensed under Gnu Public License version 2
    // Copyright (c) 2024-2025 (original work) Open Assessment Technologies SA ;

    import { __, resizeObserver } from '@oat-sa-private/ui-core';
    import SearchInput from '../searchInput/SearchInput.svelte';
    import TreeNode from './TreeNode.svelte';
    import Loading from '../loading/Loading.svelte';
    import { createEventDispatcher, setContext } from 'svelte';
    import { writable } from 'svelte/store';

    const dispatch = createEventDispatcher();

    const defaultActionableMenuItems = [
        {
            label: __('Edit'),
            key: 'edit',
            icon: 'edit-16'
        },
        {
            label: __('Create child'),
            key: 'create-child',
            icon: 'child-16'
        },
        {
            label: __('Delete'),
            key: 'delete',
            icon: 'trashcan-16'
        }
    ];

    export let data = [];
    export let nameProperty = 'name';
    export let searchable = false;
    export let searchPlaceholder = __('Search...');
    export let actionable = false;
    export let actionableLabel = __('Actions');
    //can be array or function
    export let actionableMenuItems = defaultActionableMenuItems;
    export let loading = false;
    export let maxLinesPerNode = 2;

    let search = '';
    let displayedData = data;
    let treeListElement;

    //the store will track dimensions of the tree container
    //will be set as a context to be accessible in TreeNode
    const dimensions = writable({
        width: 0
    });
    setContext('treeDimensions', dimensions);

    $: if (data) {
        if (search) {
            displayedData = structuredClone(data);
            filterTree(search);
        } else {
            displayedData = data;
        }
    }

    /**
     * Function is used to recursively filter tree
     * @param {string} searchString
     * @returns {function(TreeNode): boolean} the returned function recursively called for each of treeNode's children
     */
    function searchNode(searchString) {
        return treeNode => {
            let matchFoundInATree = false;
            if (treeNode[nameProperty].match(new RegExp(searchString, 'i'))) {
                matchFoundInATree = true;
            } else {
                matchFoundInATree = false;
            }

            //the chance is that some child has match
            if (treeNode.children) {
                treeNode.children.forEach(child => {
                    if (searchNode(searchString)(child)) {
                        matchFoundInATree = true;
                    }
                });
            }

            if (matchFoundInATree) {
                treeNode.hidden = false;
                //we need to expand the parent node to show the found node
                treeNode.expanded = true;
                treeNode.search = searchString;
            } else {
                treeNode.hidden = true;
                treeNode.expanded = false;
                treeNode.search = '';
            }

            return matchFoundInATree;
        };
    }

    /**
     *  Filters tree by the search input value
     *  @param {string} searchString
     */
    function filterTree(searchString) {
        displayedData.forEach(searchNode(searchString));
        displayedData = displayedData;
    }

    /**
     *  Updates the search value and dispatches the event
     *  @param {CustomEvent} e
     *  @emits CustomEvent
     */
    function updateSearch(e) {
        search = e.detail;
        dispatch('search', e.detail);
    }

    /**
     * Updates dimensions store with tree list element width
     */
    function treeListResized() {
        dimensions.set({ width: treeListElement?.clientWidth });
    }
</script>

<style>
    .tree-component {
        --local_searchbar-padding-left: var(--searchbar-padding-left, var(--border-medium));
        --local_gap: var(--gap, var(--space-4x));

        --color-node-hover: var(--color-mediumSilver);
        --color-node-selected: var(--color-primaryPale);
        --color-node-selected-indicator: var(--color-primaryGraphical);

        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        gap: var(--local_gap);

        & .searchbar {
            padding-left: var(--local_searchbar-padding-left);
        }

        & :global(label input[name='test-search'].key-focus-visible) {
            outline-offset: 0;
        }
    }

    ul.tree-list {
        overflow-y: auto;
        display: flex;
        padding: 0;
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;

        & :global(.flyout.tooltip-flyout .tooltip-content) {
            font-size: 1.5rem;
            line-height: 2rem;
        }
    }
</style>

<div class="tree-component" style={`--maxLinesPerNode: ${maxLinesPerNode};`}>
    {#if searchable}
        <div class="searchbar">
            <SearchInput name="test-search" placeholder={searchPlaceholder} on:change={updateSearch} />
        </div>
    {/if}
    {#if loading}
        <Loading size="small" />
    {:else}
        <ul class="tree-list" bind:this={treeListElement} use:resizeObserver={{ callback: treeListResized }}>
            {#each displayedData as node}
                <TreeNode
                    data={node}
                    {nameProperty}
                    {actionable}
                    {actionableLabel}
                    {actionableMenuItems}
                    on:toggle
                    on:select
                    on:menuAction />
            {/each}
        </ul>
    {/if}
</div>
